import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, ChevronDown, ChevronRight, ChevronUp, PlayCircle, Clock, CalendarClock } from "lucide-react";
import { adminApi, mediaUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import ImageUpload from "./ImageUpload";

const emptySeries = () => ({ title: "", description: "", image: "", order: 99, pull_quote: "", quote_by: "" });
const emptyEpisode = (series) => ({ title: "", episode: "", description: "", image: "", duration: "", date: "", youtubeId: "", series, release_date: "", featured: false });

// Extract a YouTube video ID from a pasted URL, or return the raw id
const extractYouTubeId = (input) => {
  if (!input) return "";
  const s = input.trim();
  const patterns = [/[?&]v=([^&]+)/, /youtu\.be\/([^?&]+)/, /youtube\.com\/embed\/([^?&]+)/, /youtube\.com\/shorts\/([^?&]+)/];
  for (const p of patterns) { const m = s.match(p); if (m) return m[1]; }
  return s; // assume they pasted the raw id
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const isScheduled = (rd) => rd && rd.slice(0, 10) > todayStr();

export default function SeriesManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  // series dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySeries());
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // episode dialog
  const [epOpen, setEpOpen] = useState(false);
  const [epEditing, setEpEditing] = useState(null);
  const [epForm, setEpForm] = useState(emptyEpisode(""));
  const [epLink, setEpLink] = useState("");
  const [epSaving, setEpSaving] = useState(false);
  const [epDeleteId, setEpDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([adminApi.series.list(), adminApi.podcasts.list()])
      .then(([s, p]) => { setItems(s); setEpisodes(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setEp = (k, v) => setEpForm((f) => ({ ...f, [k]: v }));

  // ---- Series CRUD ----
  const openCreate = () => { setEditing(null); setForm(emptySeries()); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptySeries(), ...it }); setOpen(true); };
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order || 99),
                        pull_quote: (form.pull_quote || "").trim(), quote_by: (form.quote_by || "").trim() };
      if (editing) await adminApi.series.update(editing.id, payload); else await adminApi.series.create(payload);
      toast({ title: `Series ${editing ? "updated" : "created"}` });
      setOpen(false); load();
    } catch { toast({ title: "Error saving", variant: "destructive" }); }
    finally { setSaving(false); }
  };
  const handleDelete = async () => {
    try { await adminApi.series.remove(deleteId); toast({ title: "Series deleted" }); load(); }
    catch { toast({ title: "Error deleting", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const move = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    try { await adminApi.series.reorder(next.map((it) => it.id)); }
    catch { toast({ title: "Couldn't save order", variant: "destructive" }); load(); }
  };

  const moveEp = async (slug, index, dir) => {
    const eps = episodesFor(slug);
    const j = index + dir;
    if (j < 0 || j >= eps.length) return;
    const reordered = [...eps];
    [reordered[index], reordered[j]] = [reordered[j], reordered[index]];
    const others = episodes.filter((e) => e.series !== slug);
    setEpisodes([...reordered, ...others]);
    try { await adminApi.podcasts.reorder(reordered.map((e) => e.id)); }
    catch { toast({ title: "Couldn't save order", variant: "destructive" }); load(); }
  };

  // ---- Episode CRUD ----
  const openEpCreate = (seriesSlug) => { setEpEditing(null); setEpForm(emptyEpisode(seriesSlug)); setEpLink(""); setEpOpen(true); };
  const openEpEdit = (ep) => { setEpEditing(ep); setEpForm({ ...emptyEpisode(ep.series), ...ep }); setEpLink(ep.youtubeId || ""); setEpOpen(true); };
  const handleEpSave = async () => {
    setEpSaving(true);
    try {
      const payload = { ...epForm, youtubeId: extractYouTubeId(epLink) };
      if (epEditing) await adminApi.podcasts.update(epEditing.id, payload); else await adminApi.podcasts.create(payload);
      toast({ title: `Episode ${epEditing ? "updated" : "added"}` });
      setEpOpen(false); load();
    } catch { toast({ title: "Error saving episode", variant: "destructive" }); }
    finally { setEpSaving(false); }
  };
  const handleEpDelete = async () => {
    try { await adminApi.podcasts.remove(epDeleteId); toast({ title: "Episode deleted" }); load(); }
    catch { toast({ title: "Error deleting", variant: "destructive" }); }
    finally { setEpDeleteId(null); }
  };

  const episodesFor = (slug) => episodes.filter((e) => e.series === slug);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Podcast Series</h1>
          <p className="text-sm text-slate-500">{items.length} series — expand a series to add episodes with their YouTube links and release dates.</p>
        </div>
        <Button onClick={openCreate} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Plus size={16} className="mr-1.5" /> New Series</Button>
      </div>

      {loading ? <p className="text-slate-400">Loading...</p> : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center text-slate-400">No series yet. Create one!</div>
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const eps = episodesFor(s.slug);
            const isOpen = expanded[s.id];
            return (
              <div key={s.id} className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                <div className="p-4 flex items-center gap-4">
                  <div className="flex flex-col shrink-0 -my-1">
                    <button data-testid={`series-up-${s.id}`} onClick={() => move(items.indexOf(s), -1)} disabled={items.indexOf(s) === 0} title="Move up" className="w-7 h-6 rounded-t-md bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-navy"><ChevronUp size={15} /></button>
                    <button data-testid={`series-down-${s.id}`} onClick={() => move(items.indexOf(s), 1)} disabled={items.indexOf(s) === items.length - 1} title="Move down" className="w-7 h-6 rounded-b-md bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-navy border-t border-white"><ChevronDown size={15} /></button>
                  </div>
                  <button onClick={() => setExpanded((e) => ({ ...e, [s.id]: !e[s.id] }))} className="text-slate-400 hover:text-navy shrink-0">
                    {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <div className="w-16 h-12 rounded-lg bg-navy overflow-hidden shrink-0">
                    {s.image && <img loading="lazy" decoding="async" src={mediaUrl(s.image)} alt="" className="w-full h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy truncate">{s.title}</p>
                    <p className="text-sm text-slate-400 truncate">{eps.length} episode(s) • /{s.slug}</p>
                  </div>
                  <button onClick={() => openEdit(s)} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy"><Pencil size={15} /></button>
                  <button onClick={() => setDeleteId(s.id)} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={15} /></button>
                </div>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-navy">Episodes</p>
                      <Button size="sm" onClick={() => openEpCreate(s.slug)} className="bg-navy hover:bg-navy-deep text-white rounded-full h-8"><Plus size={14} className="mr-1" /> Add episode</Button>
                    </div>
                    {eps.length === 0 ? (
                      <p className="text-sm text-slate-400 py-4 text-center">No episodes yet. Click "Add episode" to add the first one.</p>
                    ) : (
                      <div className="space-y-2">
                        {eps.map((ep, epIdx) => (
                          <div key={ep.id} className="bg-white rounded-lg border border-slate-100 p-3 flex items-center gap-3">
                            <div className="flex flex-col shrink-0 -my-1">
                              <button data-testid={`episode-up-${ep.id}`} onClick={() => moveEp(s.slug, epIdx, -1)} disabled={epIdx === 0} title="Move up" className="w-6 h-5 rounded-t bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-navy"><ChevronUp size={13} /></button>
                              <button data-testid={`episode-down-${ep.id}`} onClick={() => moveEp(s.slug, epIdx, 1)} disabled={epIdx === eps.length - 1} title="Move down" className="w-6 h-5 rounded-b bg-slate-100 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center text-navy border-t border-white"><ChevronDown size={13} /></button>
                            </div>
                            <PlayCircle size={18} className="text-orange shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-navy truncate">{ep.title}</p>
                              <p className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                                {ep.episode && <span>{ep.episode}</span>}
                                {ep.release_date && (
                                  <span className={`inline-flex items-center gap-1 ${isScheduled(ep.release_date) ? "text-amber-500" : "text-green-600"}`}>
                                    <CalendarClock size={11} /> {isScheduled(ep.release_date) ? `scheduled ${ep.release_date}` : `live ${ep.release_date}`}
                                  </span>
                                )}
                              </p>
                            </div>
                            <button onClick={() => openEpEdit(ep)} className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-navy"><Pencil size={14} /></button>
                            <button onClick={() => setEpDeleteId(ep.id)} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={14} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Series dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Series" : "New Series"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-navy font-semibold">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-2" placeholder="e.g. Making Tax Digital Series" /></div>
            <div><Label className="text-navy font-semibold">Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-2" /></div>
            <div><Label className="text-navy font-semibold">Sort order</Label><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="mt-2" /></div>
            <ImageUpload label="Cover image" value={form.image} onChange={(v) => set("image", v)} />
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <Label className="text-navy font-semibold">Pull quote (optional)</Label>
              <Textarea
                rows={3}
                value={form.pull_quote || ""}
                onChange={(e) => set("pull_quote", e.target.value)}
                data-testid="series-pull-quote"
                className="mt-2"
                placeholder="One line worth quoting from this series."
              />
              <Input
                value={form.quote_by || ""}
                onChange={(e) => set("quote_by", e.target.value)}
                data-testid="series-quote-by"
                className="mt-2"
                placeholder="Who said it — name and business"
              />
              <p className="text-xs text-slate-400 mt-2 leading-relaxed">
                While this is filled in, a link to this series shared on LinkedIn or WhatsApp shows a quote card
                instead of the cover image. Clear it to go back to the artwork.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Save size={15} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Episode dialog */}
      <Dialog open={epOpen} onOpenChange={setEpOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{epEditing ? "Edit Episode" : "Add Episode"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-navy font-semibold">Episode title</Label><Input value={epForm.title} onChange={(e) => setEp("title", e.target.value)} className="mt-2" /></div>
            <div><Label className="text-navy font-semibold">Episode label</Label><Input value={epForm.episode} onChange={(e) => setEp("episode", e.target.value)} className="mt-2" placeholder="Episode 1" /></div>
            <div>
              <Label className="text-navy font-semibold">YouTube link or ID</Label>
              <Input value={epLink} onChange={(e) => setEpLink(e.target.value)} className="mt-2" placeholder="Paste the full YouTube URL or the video ID" />
              <p className="text-xs text-slate-400 mt-1">e.g. https://www.youtube.com/watch?v=abc123 — we'll extract the ID automatically.</p>
            </div>
            <div><Label className="text-navy font-semibold">Description</Label><Textarea rows={3} value={epForm.description} onChange={(e) => setEp("description", e.target.value)} className="mt-2" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-navy font-semibold">Duration</Label><Input value={epForm.duration} onChange={(e) => setEp("duration", e.target.value)} className="mt-2" placeholder="34 min" /></div>
              <div><Label className="text-navy font-semibold">Date</Label><Input type="date" value={epForm.date ? epForm.date.slice(0,10) : ""} onChange={(e) => setEp("date", e.target.value)} className="mt-2" /></div>
            </div>
            <div>
              <Label className="text-navy font-semibold">Release date (view from)</Label>
              <Input type="date" value={epForm.release_date ? epForm.release_date.slice(0,10) : ""} onChange={(e) => setEp("release_date", e.target.value)} className="mt-2" />
              <p className="text-xs text-slate-400 mt-1">Episode stays hidden on the website until this date. Leave blank to show immediately.</p>
            </div>
            <div><Label className="text-navy font-semibold">Thumbnail image URL</Label><Input value={epForm.image} onChange={(e) => setEp("image", e.target.value)} className="mt-2" placeholder="Optional image URL" /></div>
            <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
              <div>
                <Label className="text-navy font-semibold">Show under "Latest Episodes"</Label>
                <p className="text-xs text-slate-400 mt-0.5">Feature this episode on the podcast main page.</p>
              </div>
              <Switch checked={!!epForm.featured} onCheckedChange={(v) => setEp("featured", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEpOpen(false)} className="rounded-full"><X size={15} className="mr-1" /> Cancel</Button>
            <Button onClick={handleEpSave} disabled={epSaving} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Save size={15} className="mr-1" /> {epSaving ? "Saving..." : "Save Episode"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this series?</AlertDialogTitle><AlertDialogDescription>Episodes will remain but will no longer be grouped under it.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!epDeleteId} onOpenChange={(o) => !o && setEpDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this episode?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEpDelete} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
